import ReturnOrderDetails from "@dana/Components/StandardReturnsOrderStatus/ReturnOrderDetails";
import RMADetailsView from "@dana/Components/StandardReturnsOrderStatus/RMADetailsView";
import ExportIcon from "@dana/Icons/ExportIcon";
import VectorIcon from "@dana/Icons/VectorIcon";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { GetStandardReturnHistoryParameter, StandardReturnOrder } from "@dana/Services/StandardReturnStatusService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import getStandardReturnOrderDetails from "@dana/Store/Data/StandardReturnsStatus/Handlers/StandardReturnOrderDetailsHandler";
import updateStandardReturnsHistoryFilter from "@dana/Store/Data/StandardReturnsStatus/Handlers/StandardReturnsStatusFilterHandler";
import getStandardReturnOrders from "@dana/Store/Data/StandardReturnsStatus/Handlers/StandardReturnsStatusHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getOrders, GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import DatePicker, { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { DatePickerState } from "@insite/mobius/DatePicker/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import {
    Checkbox,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Link,
    LoadingOverlay,
    Modal,
    Pagination,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@insite/mobius/index";
import { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Styles Interfaces and Objects ---
export interface ReturnStatusStyles {
    wrapper?: InjectableCss;
    datePickerWrapper?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    tableWrap?: InjectableCss;
    dataTable?: DataTableProps;
    tableHeader?: DataTableHeaderProps;
    tableRow?: DataTableRowProps;
    tableCell?: DataTableCellProps;
    tableCellErpNumber?: DataTableCellProps;
    statusIndicator?: InjectableCss;
    tooltipText?: TooltipPresentationProps;
    searchInput?: TextFieldPresentationProps;
    rmaNoLink: LinkPresentationProps;
    exportButton?: ButtonPresentationProps;
    disabledRow?: InjectableCss;
    messagePill?: TypographyPresentationProps;
    modal?: ModalPresentationProps;
    orderStatusFromDate?: DatePickerPresentationProps;
    orderStatusToDate?: DatePickerPresentationProps;
    orderStatusGoBtn?: ButtonIconPresentationProps;
    orderStatusCheckDay?: RadioPresentationProps;
    orderStatusCheckSpecify?: RadioPresentationProps;
    orderStatusRadioBoxWrapper?: RadioGroupProps;
}

export const ReturnStatusStyles: ReturnStatusStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    datePickerWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 32px;
            margin: 24px 0;

            & > div {
                flex: 0 1 30%;
            }

            @media only screen and (max-width: 768px) {
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;

                & > div,
                & > button {
                    flex: auto;
                    width: 100%;
                }
            }
        `,
    },
    datePicker: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    tableWrap: {
        css: css`
            overflow: auto;
            position: relative;
        `,
    },
    tableCell: {
        typographyProps: {
            size: fontSizes.small,
            lineHeight: lineHeights.default,
        },
    },
    tableCellErpNumber: {
        typographyProps: {
            size: fontSizes.small,
            lineHeight: lineHeights.default,
            color: themeColors.primary.danaBlue,
            weight: fontWeights.semiBold,
            css: css`
                text-decoration: none;
                cursor: pointer;

                &:hover {
                    color: ${themeColors.secondary.danaDarkBlue};
                }
            `,
        },
    },
    statusIndicator: {
        css: css`
            width: 32px;
            height: 6px;
            background: ${themeBackgrounds.lightGray};
            border-radius: 4px;
            margin: 0 auto;

            &.success {
                background: ${themeColors.successGreen};
            }
        `,
    },
    tooltipText: {
        mergeCss: true,
        cssOverrides: {
            tooltipBody: css`
                font-weight: ${fontWeights.regular};
            `,
            tooltipContainer: css`
                width: auto;
                max-width: 220px;
            `,
        },
    },
    searchInput: {
        iconProps: { src: Search },
        mergeCss: true,
    },
    rmaNoLink: {
        typographyProps: {
            size: fontSizes.small,
            lineHeight: lineHeights.default,
            underline: false,
            weight: fontWeights.semiBold,
        },
        css: css`
            ${StyledTypography} {
                text-decoration: none;
            }

            [class*="TooltipWrapper"] {
                margin-left: 8px;
            }
        `,
    },
    exportButton: {
        variant: "tertiary",
        icon: {
            src: ExportIcon,
            position: "left",
        },
        css: css`
            ${IconWrapper} {
                margin-right: 0;
            }
        `,
    },
    disabledRow: {
        css: css`
            td:not(.rma-no-cell) {
                opacity: 0.55;
                cursor: not-allowed;
            }
        `,
    },
    messagePill: {
        css: css`
            margin-left: 12px;
            background: ${themeColors.successGreen};
            padding: 4px 8px;
            border-radius: 14px;
            font-size: ${fontSizes.xSmall};
            line-height: ${lineHeights.default};
            font-weight: ${fontWeights.semiBold};
            color: ${themeColors.primary.white};
            text-transform: uppercase;
            display: inline-block;
            letter-spacing: 0.5px;
        `,
    },
    modal: {
        size: 1140,
        sizeVariant: "large",
        headlineTypographyProps: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    orderStatusCheckDay: {
        css: css`
            margin-top: 0px;
        `,
    },
    orderStatusCheckSpecify: {
        css: css`
            margin-top: 0px !important;
        `,
    },
    orderStatusRadioBoxWrapper: {
        css: css`
            flex-direction: row;
            gap: 10px;
            input {
                margin-left: 0;
            }
        `,
    },
    orderStatusFromDate: {
        cssOverrides: {
            formInputWrapper: css`
                width: 100%;
            `,
        },
    },

    orderStatusToDate: {
        cssOverrides: {
            formInputWrapper: css`
                width: 100%;
            `,
        },
    },
};

const styles = ReturnStatusStyles;

const mapStateToProps = (state: ApplicationStateDana) => ({
    orderStatusMappingDataView: getOrderStatusMappingDataView(state),
    language: state.context.session.language,
    orderDetailsUrl: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
    standardReturnsHistory: state.standardReturnsHistoryState,
});

const mapDispatchToProps = {
    loadOrderStatusMappings,
    getStandardReturnOrders,
    updateStandardReturnsHistoryFilter,
    getStandardReturnOrderDetails,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
const StandardReturnsStatus: React.FC<Props> = ({
    language,
    standardReturnsHistory,
    getStandardReturnOrders,
    updateStandardReturnsHistoryFilter,
    getStandardReturnOrderDetails,
}) => {
    const [selectedOption, setSelectedOption] = useState<"30days" | "dateRange">("30days");
    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();
    const [dateOrdered, setDateOrdered] = useState<Date | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    const [orders, setOrders] = useState<StandardReturnOrder[]>([]);
    const [pagination, setPagination] = useState<PaginationModel | null>(null);
    const [selectedStatuses, setSelectedStatuses] = useState<string>();

    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [modalType, setModalType] = useState<string>("");
    const [modalTitle, setModalTitle] = useState<string>("");

    const disabledProps = styles.disabledRow;
    const toasterContext = React.useContext(ToasterContext);
    useEffect(() => {
        setOrders(standardReturnsHistory?.standardReturns);
        setPagination(standardReturnsHistory?.pagination);
        setIsLoading(standardReturnsHistory?.isLoading);
        setSelectedStatuses(standardReturnsHistory.filter.statuses);
    }, [standardReturnsHistory?.standardReturns, standardReturnsHistory?.isLoading]);

    const set30daysDateRange = () => {
        const today = new Date();
        const lastMonthDate = new Date(today);
        lastMonthDate.setDate(today.getDate() - 30);
        setFromDate(lastMonthDate);
        setToDate(today);
        return { fromDate: lastMonthDate, toDate: today };
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        if (value === "30days" || value === "dateRange") {
            setSelectedOption(value);
            if (value === "30days") {
                const dateRange = set30daysDateRange();
                searchOrders({
                    ...standardReturnsHistory.filter,
                    fromDate: convertDateToDateOnlyString(dateRange.fromDate),
                    toDate: convertDateToDateOnlyString(dateRange.toDate),
                });
            }
        }
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) {
            setFromDate(selectedDay);
            searchOrders({
                ...standardReturnsHistory.filter,
                fromDate: convertDateToDateOnlyString(selectedDay),
            });

            // If the new start date is after the existing end date, clear the end date
            if (toDate && selectedDay > toDate) {
                setToDate(undefined);
            }
        }
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) {
            setToDate(selectedDay);
            searchOrders({
                ...standardReturnsHistory.filter,
                toDate: convertDateToDateOnlyString(selectedDay),
            });
        }
    };
    const orderDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setDateOrdered(selectedDay);
        searchOrders({
            ...standardReturnsHistory.filter,
            orderDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const searchOrders = (filter?: GetStandardReturnHistoryParameter) => {
        if (!filter) {
            filter = { ...standardReturnsHistory.filter };
        } else {
            updateStandardReturnsHistoryFilter(filter);
        }
        getStandardReturnOrders({
            ...filter,
        });
    };

    const onChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (Number(event.currentTarget.value)) {
            const newFilter = {
                ...standardReturnsHistory.filter,
                pageSize: Number(event.currentTarget.value),
            };
            searchOrders(newFilter);
        }
    };

    const headerClick = (sortField: string) => {
        const previousSort = standardReturnsHistory.filter.sort;
        const direction = previousSort && previousSort === "ASC" ? "DESC" : "ASC";
        const newFilter = {
            ...standardReturnsHistory.filter,
            sortBy: sortField,
            sort: direction,
        } as GetStandardReturnHistoryParameter;
        searchOrders(newFilter);
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (standardReturnsHistory.filter.sortBy !== sortField) {
            return sorted;
        }
        const primaryField = standardReturnsHistory.filter.sort;
        if (primaryField === "ASC") {
            sorted = "ascending";
        } else if (primaryField === "DESC") {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const onChangePage = (page: number) => {
        const filter = {
            ...standardReturnsHistory.filter,
            page,
        };
        searchOrders(filter);
    };
    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const filter = {
            ...standardReturnsHistory.filter,
            [name]: value,
        };
        searchOrders(filter);
    };

    const clearSearch = (inputType: string) => {
        if (inputType === "erpOrderNumber") {
            searchOrders({
                ...standardReturnsHistory.filter,
                erpOrderNumber: "",
            });
        }

        if (inputType === "customerPO") {
            searchOrders({
                ...standardReturnsHistory.filter,
                customerPO: "",
            });
        }

        if (inputType === "webOrderNumber") {
            searchOrders({
                ...standardReturnsHistory.filter,
                webOrderNumber: "",
            });
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const dateRange = set30daysDateRange();

            const initialFilters = {
                fromDate: convertDateToDateOnlyString(dateRange.fromDate),
                toDate: convertDateToDateOnlyString(dateRange.toDate),
                erpOrderNumber: params.get("erpOrderNumber") ?? undefined,
                dateOrdered: params.get("dateOrdered") ?? undefined,
                customerPO: params.get("customerPO") ?? undefined,
                webOrderNumber: params.get("webOrderNumber") ?? undefined,
                statuses: params.get("statuses") ?? undefined,
            } as GetStandardReturnHistoryParameter;

            searchOrders(initialFilters);
        }
    }, []);
    const getStandardReturnOrderDetailsHandler = (orderNumber: string) => {
        openModalForOrder("OrderDetails");
        setModalTitle("Returns Order Details");
        getStandardReturnOrderDetails({ orderNumber, additionalExpands: ["standardReturn"] });
    };
    const getStandardReturnRMAHandler = (orderNumber: string) => {
        openModalForOrder("ExportRma");
        setModalTitle(`RMA for ${orderNumber}`);
        getStandardReturnOrderDetails({ orderNumber, additionalExpands: ["rma"] });
    };

    const openModalForOrder = (modalType: string) => {
        setModalOpen(true);
        if (modalType === "OrderDetails") {
            setModalType("OrderDetails");
        } else {
            setModalType("ExportRma");
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalType("");
    };
    const [minOrderDate, setMinOrderDate] = useState<Date | undefined>();
    useEffect(() => {
        if (fromDate) {
            const lastMonthDate = new Date(fromDate);
            lastMonthDate.setDate(fromDate.getDate() + 30);
            setMinOrderDate(lastMonthDate);
        }
    }, [toDate, fromDate]);

    const handleStatusChange = (isChecked: boolean, status: string) => {
        // Split into array, or empty if nothing selected
        let selectedArr = selectedStatuses ? selectedStatuses.split(",") : [];
        if (isChecked) {
            // Add to array if not present
            if (!selectedArr.includes(status)) {
                selectedArr.push(status);
            }
        } else {
            // Remove unchecked status
            selectedArr = selectedArr.filter(s => s !== status);
        }
        // Convert array back to comma-separated string
        setSelectedStatuses(selectedArr.join(","));
        const newFilter = {
            ...standardReturnsHistory.filter,
            statuses: selectedArr.join(","),
        } as GetStandardReturnHistoryParameter;
        searchOrders(newFilter);
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.wrapper}>
                <RadioGroup
                    value={selectedOption}
                    {...styles.orderStatusRadioBoxWrapper}
                    onChangeHandler={handleRadioChange}
                >
                    <Radio value="30days" {...styles.orderStatusCheckDay}>
                        <span className="switch-label">{translate("30 Days")}</span>
                    </Radio>
                    <Radio value="dateRange" {...styles.orderStatusCheckSpecify}>
                        <span className="switch-label">{translate("Specify Date Range")}</span>
                    </Radio>
                </RadioGroup>
                <StyledWrapper {...styles.datePickerWrapper}>
                    <DatePicker
                        {...styles.orderStatusFromDate}
                        disabled={selectedOption === "30days"}
                        selectedDay={fromDate || undefined}
                        onDayChange={fromDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            maxDate: toDate,
                        }}
                    />
                    <DatePicker
                        {...styles.orderStatusToDate}
                        disabled={selectedOption === "30days"}
                        selectedDay={toDate || undefined}
                        onDayChange={toDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: minOrderDate,
                        }}
                    />
                    <Button {...styles.orderStatusGoBtn} onClick={() => searchOrders()}>
                        {translate("Go")}
                    </Button>
                </StyledWrapper>
                <StyledWrapper {...styles.tableWrap}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader
                                tight={true}
                                sorted={sorted("OrderDate")}
                                onSortClick={() => headerClick("OrderDate")}
                            >
                                {translate("Return Order Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                tight={true}
                                sorted={sorted("ErpOrderNumber")}
                                onSortClick={() => headerClick("ErpOrderNumber")}
                            >
                                {translate("RMA Number")}
                            </DataTableHeader>
                            <DataTableHeader
                                tight={true}
                                sorted={sorted("CustomerPO")}
                                onSortClick={() => headerClick("CustomerPO")}
                            >
                                {translate("Return Primary PO No.")}
                            </DataTableHeader>
                            <DataTableHeader
                                tight={true}
                                sorted={sorted("WebOrderNumber")}
                                onSortClick={() => headerClick("WebOrderNumber")}
                            >
                                {translate("Process No.")}
                            </DataTableHeader>
                            <DataTableHeader tight={true}>{translate("Returns Type")}</DataTableHeader>
                            <DataTableHeader tight={true}>{translate("View/Print Documents")}</DataTableHeader>
                            <DataTableHeader tight={true} colSpan={3}>
                                {translate("Overall Status")}
                                <Typography className="message-pill" {...styles.messagePill}>
                                    {translate("Coming Soon")}
                                </Typography>
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableHead>
                            <DataTableHeader tight={true}>
                                <DatePicker
                                    {...styles.orderStatusFromDate}
                                    // disabled={selectedOption === "dateRange"}
                                    selectedDay={dateOrdered || undefined}
                                    onDayChange={orderDateChangeHandler}
                                    data-test-selector="return_status_date"
                                    dateTimePickerProps={{
                                        minDate: fromDate,
                                        maxDate: toDate,
                                    }}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true}>
                                <TextField
                                    name="erpOrderNumber"
                                    type="text"
                                    value={standardReturnsHistory.filter.erpOrderNumber}
                                    onChange={handleTextChange}
                                    {...styles.searchInput}
                                    data-test-selector="retun_status_rma_no"
                                    iconProps={{
                                        src:
                                            standardReturnsHistory.filter.erpOrderNumber !== undefined
                                                ? VectorIcon
                                                : Search,
                                    }}
                                    iconClickableProps={{
                                        onClick: () => {
                                            standardReturnsHistory.filter.erpOrderNumber !== undefined
                                                ? clearSearch("erpOrderNumber")
                                                : undefined;
                                        },
                                    }}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true}>
                                <TextField
                                    {...styles.searchInput}
                                    name="customerPO"
                                    type="text"
                                    value={standardReturnsHistory.filter.customerPO}
                                    onChange={handleTextChange}
                                    data-test-selector="return_status_primary_pono"
                                    iconProps={{
                                        src:
                                            standardReturnsHistory.filter.customerPO !== undefined
                                                ? VectorIcon
                                                : Search,
                                    }}
                                    iconClickableProps={{
                                        onClick: () => {
                                            standardReturnsHistory.filter.customerPO !== undefined
                                                ? clearSearch("customerPO")
                                                : undefined;
                                        },
                                    }}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true}>
                                <TextField
                                    {...styles.searchInput}
                                    name="webOrderNumber"
                                    type="text"
                                    value={standardReturnsHistory.filter.webOrderNumber}
                                    onChange={handleTextChange}
                                    data-test-selector="return_status_process_no"
                                    iconProps={{
                                        src:
                                            standardReturnsHistory.filter.webOrderNumber !== undefined
                                                ? VectorIcon
                                                : Search,
                                    }}
                                    iconClickableProps={{
                                        onClick: () => {
                                            standardReturnsHistory.filter.webOrderNumber !== undefined
                                                ? clearSearch("webOrderNumber")
                                                : undefined;
                                        },
                                    }}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true}></DataTableHeader>
                            <DataTableHeader tight={true} alignX="center">
                                <Tooltip {...styles.tooltipText} text={translate("View/Print: RMA")} />
                            </DataTableHeader>
                            <DataTableHeader tight={true} alignX="center">
                                <Typography as="p">{translate("Not Processed")}</Typography>
                                <Checkbox
                                    checked={standardReturnsHistory.filter?.statuses?.split(",")?.includes("A")}
                                    onChange={(e, value) => handleStatusChange(value, "A")}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true} alignX="center">
                                <Typography as="p">{translate("Partially Processed")}</Typography>
                                <Checkbox
                                    checked={standardReturnsHistory.filter?.statuses?.split(",").includes("B")}
                                    onChange={(e, value) => handleStatusChange(value, "B")}
                                />
                            </DataTableHeader>
                            <DataTableHeader tight={true} alignX="center">
                                <Typography as="p">{translate("Completed")}</Typography>
                                <Checkbox
                                    checked={standardReturnsHistory.filter?.statuses?.split(",").includes("C")}
                                    onChange={(e, value) => handleStatusChange(value, "C")}
                                />
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {orders?.map((order: StandardReturnOrder) => (
                                <DataTableRow key={order.webOrderNumber}>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        {order.orderDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(order.orderDate),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.tableCellErpNumber}
                                        tight={true}
                                        className="rma-no-cell"
                                        onClick={() => getStandardReturnOrderDetailsHandler(order.erpOrderNumber)}
                                    >
                                        {order.erpOrderNumber ?? translate("N/A")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        {order.customerPO ?? translate("N/A")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        {order.webOrderNumber ?? translate("N/A")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        {translate("Standard Return")}
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true} alignX="center">
                                        <Button
                                            {...styles.exportButton}
                                            onClick={() => getStandardReturnRMAHandler(order.erpOrderNumber)}
                                        ></Button>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        <StyledWrapper
                                            {...styles.statusIndicator}
                                            className={order.status === "A" ? "success" : ""}
                                        ></StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        <StyledWrapper
                                            {...styles.statusIndicator}
                                            className={order.status === "B" ? "success" : ""}
                                        ></StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCell} tight={true}>
                                        <StyledWrapper
                                            {...styles.statusIndicator}
                                            className={order.status === "C" ? "success" : ""}
                                        ></StyledWrapper>
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                    {(orders?.length === 0 || !orders )&& !isLoading && (
                        <StyledWrapper>
                            <p>{translate("No Standard Return Orders Found.")}</p>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                {orders && orders?.length > 0 && pagination && (
                    <Pagination
                        currentPage={pagination.currentPage}
                        resultsPerPage={pagination.pageSize}
                        resultsCount={pagination.totalItemCount}
                        resultsPerPageOptions={pagination.pageSizeOptions}
                        onChangePage={onChangePage}
                        resultsPerPageLabel={translate("Display")}
                        onChangeResultsPerPage={onChangeResultsPerPage}
                        prevPage={pagination.prevPageUri}
                        nextPage={pagination.nextPageUri}
                    />
                )}
                <Modal headline={translate(modalTitle)} isOpen={isModalOpen} handleClose={closeModal} {...styles.modal}>
                    {modalType === "OrderDetails" ? <ReturnOrderDetails /> : <RMADetailsView />}
                </Modal>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(StandardReturnsStatus);
